import React, { useRef, useState } from 'react';
import { View, TouchableOpacity, StyleSheet, KeyboardAvoidingView, Platform, ScrollView, ActivityIndicator } from 'react-native';
import { Text, TextInput } from '../components/AppText';
import { SafeAreaView } from 'react-native-safe-area-context';
import { Ionicons } from '@expo/vector-icons';
import { supabase } from '../lib/supabase';
import { Colors } from '../theme';
import { FadeIn, PressableScale } from '../components/Motion';

const LOGIN_TIMEOUT_MS = 15000;

export function AuthScreen() {
  const scrollRef = useRef<ScrollView>(null);
  const [email,    setEmail]    = useState('');
  const [password, setPassword] = useState('');
  const [loading,  setLoading]  = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [focusedField, setFocusedField] = useState<'email' | 'password' | null>(null);
  const passwordRef = useRef<TextInput>(null);

  const friendlyError = (msg: string) => {
    if (msg.includes('Invalid login credentials'))
      return 'E-mail ou senha incorretos.';
    if (msg.includes('Email not confirmed'))
      return 'Confirme seu e-mail antes de entrar.';
    if (msg.includes('rate limit') || msg.includes('too many'))
      return 'Muitas tentativas. Aguarde alguns minutos.';
    if (/type\s*error|failed to fetch|network request failed|load failed|fetch failed|non iso-8859-1/i.test(msg))
      return 'Não foi possível conectar. Verifique sua internet e tente novamente.';
    return msg;
  };

  const handleLogin = async () => {
    if (!email.trim() || !password) {
      setErrorMessage('Informe o e-mail e a senha para entrar.');
      return;
    }
    setLoading(true);
    setErrorMessage(null);
    try {
      const result = await Promise.race([
        supabase.auth.signInWithPassword({ email: email.trim().toLowerCase(), password }),
        new Promise<never>((_, reject) => {
          setTimeout(() => reject(new Error('LOGIN_TIMEOUT')), LOGIN_TIMEOUT_MS);
        }),
      ]);
      if (result.error) setErrorMessage(friendlyError(result.error.message));
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      setErrorMessage(
        message === 'LOGIN_TIMEOUT'
          ? 'O servidor demorou para responder. Confira sua conexão e tente novamente.'
          : friendlyError(message),
      );
    } finally {
      setLoading(false);
    }
  };

  const focusFormField = () => {
    // iOS PWA keyboards do not always trigger a native resize event. Keeping
    // the form scrolled to its end exposes the password field and action.
    setTimeout(() => scrollRef.current?.scrollToEnd({ animated: true }), 80);
  };

  return (
    <View style={s.root}>
      <SafeAreaView
        style={[{ flex: 1 }, Platform.OS === 'web' && s.webSafe]}
        edges={Platform.OS === 'web' ? [] : ['bottom']}
      >
        <KeyboardAvoidingView
          style={{ flex: 1 }}
          behavior={Platform.OS === 'android' ? 'height' : 'padding'}
        >
          <ScrollView
            ref={scrollRef}
            style={s.scrollView}
            contentContainerStyle={s.scroll}
            horizontal={false}
            keyboardShouldPersistTaps="handled"
            keyboardDismissMode="interactive"
            automaticallyAdjustKeyboardInsets
            contentInsetAdjustmentBehavior="automatic"
            showsVerticalScrollIndicator={false}
            showsHorizontalScrollIndicator={false}
          >
            {/* Hero */}
            <View style={s.hero}>
              <View style={[s.ring, s.ringLg]} />
              <View style={[s.ring, s.ringMd]} />
              <View style={[s.ring, s.ringSm]} />
              <FadeIn delay={60} distance={12}>
                <View style={s.heroCopy}>
                  <View style={s.logoBadge}>
                    <Ionicons name="home-outline" size={25} color="#FFFFFF" />
                  </View>
                  <Text style={s.logoTitle} accessibilityRole="header">Casa em Dia</Text>
                  <Text style={s.logoSub}>As contas da casa, organizadas a dois.</Text>
                </View>
              </FadeIn>
            </View>

            {/* Form */}
            <FadeIn delay={140} distance={16}>
              <View style={s.form}>
                <View style={s.formHeader}>
                  <Text style={s.formTitle}>Entrar</Text>
                  <Text style={s.formSub}>Use o e-mail da conta da casa.</Text>
                </View>

                <View style={s.field}>
                  <Text style={s.fieldLabel} nativeID="label-email">E-mail</Text>
                  <View style={[s.inputRow, focusedField === 'email' && s.inputRowFocused]}>
                    <Ionicons name="mail-outline" size={17} color={focusedField === 'email' ? Colors.primary : Colors.text3} />
                    <TextInput
                      style={s.input}
                      placeholder="seu@email.com"
                      placeholderTextColor={Colors.text3}
                      value={email}
                      onChangeText={setEmail}
                      keyboardType="email-address"
                      autoCapitalize="none"
                      autoCorrect={false}
                      autoComplete="email"
                      textContentType="username"
                      returnKeyType="next"
                      accessibilityLabel="E-mail"
                      accessibilityLabelledBy="label-email"
                      onSubmitEditing={() => passwordRef.current?.focus()}
                      onFocus={() => { setFocusedField('email'); focusFormField(); }}
                      onBlur={() => setFocusedField(null)}
                    />
                  </View>
                </View>

                <View style={s.field}>
                  <Text style={s.fieldLabel} nativeID="label-password">Senha</Text>
                  <View style={[s.inputRow, s.inputRowPassword, focusedField === 'password' && s.inputRowFocused]}>
                    <Ionicons name="lock-closed-outline" size={17} color={focusedField === 'password' ? Colors.primary : Colors.text3} />
                    <TextInput
                      ref={passwordRef}
                      style={s.input}
                      placeholder="Sua senha"
                      placeholderTextColor={Colors.text3}
                      value={password}
                      onChangeText={setPassword}
                      secureTextEntry={!showPassword}
                      autoComplete="password"
                      textContentType="password"
                      returnKeyType="done"
                      accessibilityLabel="Senha"
                      accessibilityLabelledBy="label-password"
                      onSubmitEditing={handleLogin}
                      onFocus={() => { setFocusedField('password'); focusFormField(); }}
                      onBlur={() => setFocusedField(null)}
                    />
                    <TouchableOpacity
                      onPress={() => setShowPassword(value => !value)}
                      accessibilityRole="button"
                      accessibilityLabel={showPassword ? 'Ocultar senha' : 'Mostrar senha'}
                      style={s.eyeButton}
                    >
                      <Ionicons name={showPassword ? 'eye-off-outline' : 'eye-outline'} size={18} color={Colors.text2} />
                    </TouchableOpacity>
                  </View>
                </View>

                {errorMessage ? (
                  <View style={s.errorBox} accessibilityLiveRegion="polite">
                    <Ionicons name="alert-circle-outline" size={16} color={Colors.expense} />
                    <Text style={s.errorText}>{errorMessage}</Text>
                  </View>
                ) : null}

                <PressableScale
                  containerStyle={[s.btn, loading && { opacity: 0.7 }]}
                  contentStyle={s.btnContent}
                  onPress={handleLogin}
                  disabled={loading}
                  accessibilityRole="button"
                  accessibilityLabel="Entrar"
                >
                  {loading
                    ? <ActivityIndicator color="#fff" />
                    : (
                      <>
                        <Text style={s.btnText}>Entrar</Text>
                        <Ionicons name="arrow-forward" size={17} color="#fff" />
                      </>
                    )
                  }
                </PressableScale>

                <Text style={s.footer}>Acesso restrito aos membros da casa.</Text>
              </View>
            </FadeIn>
          </ScrollView>
        </KeyboardAvoidingView>
      </SafeAreaView>
    </View>
  );
}

const s = StyleSheet.create({
  root: { flex: 1, backgroundColor: Colors.bg },
  scrollView: {
    width: '100%',
    maxWidth: '100%',
    minWidth: 0,
    overflowX: 'hidden',
  } as any,
  scroll: {
    width: '100%',
    maxWidth: '100%',
    minWidth: 0,
    boxSizing: 'border-box',
    flexGrow: 1,
    minHeight: '100%' as any,
  },
  webSafe: {
    paddingBottom: 'env(safe-area-inset-bottom, 0px)',
    boxSizing: 'border-box',
  } as any,
  hero: {
    minHeight: 270,
    position: 'relative',
    overflow: 'hidden',
    justifyContent: 'flex-end',
    backgroundColor: Colors.dark,
    borderBottomLeftRadius: 29,
    borderBottomRightRadius: 29,
    paddingHorizontal: 25,
    paddingBottom: 31,
    paddingTop: Platform.OS === 'web' ? ('calc(env(safe-area-inset-top, 0px) + 56px)' as any) : 88,
  },
  ring: { position: 'absolute', borderWidth: 1.5, borderRadius: 999 },
  ringSm: { width: 162, height: 162, right: -60, top: -50, borderColor: 'rgba(255,255,255,0.06)' },
  ringMd: { width: 270, height: 270, right: -120, top: -110, borderColor: 'rgba(255,255,255,0.05)' },
  ringLg: { width: 378, height: 378, right: -180, top: -170, borderColor: 'rgba(255,255,255,0.04)' },
  heroCopy: { gap: 9 },
  logoBadge: {
    width: 54,
    height: 54,
    borderRadius: 18,
    backgroundColor: Colors.primary,
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 7,
  },
  logoTitle: { color: '#FFFFFF', fontSize: 29, lineHeight: 32, fontWeight: '800', letterSpacing: -0.9 },
  logoSub: { color: '#C9D1DE', fontSize: 14, lineHeight: 21 },
  form: {
    width: '100%',
    maxWidth: 520,
    alignSelf: 'center',
    paddingHorizontal: 22,
    paddingTop: 29,
    paddingBottom: 25,
    gap: 16,
  },
  formHeader: { gap: 4 },
  formTitle: { color: Colors.text1, fontSize: 22, fontWeight: '800', letterSpacing: -0.54 },
  formSub: { color: Colors.text2, fontSize: 14, lineHeight: 19 },
  field: { gap: 7 },
  fieldLabel: { color: Colors.text1, fontSize: 13, fontWeight: '600' },
  inputRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 9,
    height: 49,
    borderRadius: 13,
    borderWidth: 1,
    borderColor: Colors.border,
    backgroundColor: Colors.surface,
    paddingHorizontal: 14,
  },
  inputRowPassword: { paddingRight: 5 },
  inputRowFocused: { borderWidth: 2, borderColor: Colors.primary, paddingHorizontal: 14 },
  input: { flex: 1, minWidth: 0, height: 45, color: Colors.text1, fontSize: 14 },
  eyeButton: { width: 44, height: 44, borderRadius: 11, alignItems: 'center', justifyContent: 'center' },
  btn: {
    backgroundColor: Colors.primary,
    borderRadius: 14,
    minHeight: 49,
    justifyContent: 'center',
    marginTop: 5,
    shadowColor: Colors.primary,
    shadowOffset: { width: 0, height: 9 },
    shadowOpacity: 0.25,
    shadowRadius: 22,
    elevation: 6,
  },
  btnContent: { flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 7, minHeight: 49 },
  btnText: { color: '#FFFFFF', fontSize: 14, fontWeight: '700' },
  errorBox: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 7,
    backgroundColor: Colors.expenseLt,
    borderRadius: 11,
    borderWidth: 1,
    borderColor: '#F2C4CC',
    paddingHorizontal: 11,
    paddingVertical: 9,
  },
  errorText: { flex: 1, color: Colors.expenseDk, fontSize: 13, lineHeight: 17 },
  footer: { marginTop: 7, textAlign: 'center', color: Colors.text3, fontSize: 12 },
});
